import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { tap } from 'rxjs';
import { UserMaintenanceRoutes } from 'src/app/core/enums/routes.enum';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { redirectAndBypassUnsavedDataCheck } from 'src/app/shared/functions/redirect-and-bypass-unsaved-data-check';
import { IServiceReport } from '../models/service-report.model';
import { CurrentComponentIdService } from '../services/current-component-id.service';
import { ServiceReportApiService } from '../services/service-report-api.service';
import {
  CreateServiceReport,
  DeleteServiceReport,
  GetServiceReportById,
  PrintServiceReport,
  UpdateServiceReport,
} from './service-report.actions';

export interface ServiceReportStateModel {
  serviceReport: IServiceReport;
  serviceReportFile: Blob;
}

const SERVICE_REPORT_STATE_TOKEN = new StateToken<ServiceReportStateModel>('serviceReports');

@State<ServiceReportStateModel>({
  name: SERVICE_REPORT_STATE_TOKEN,
  defaults: {
    serviceReport: <IServiceReport>{},
    serviceReportFile: <Blob>{},
  },
})
@Injectable()
export class ServiceReportState {
  private get currentComponentId(): string {
    return this.componentIdService.get();
  }

  constructor(
    private service: ServiceReportApiService,
    private componentIdService: CurrentComponentIdService
  ) {}

  @Action(GetServiceReportById, { cancelUncompleted: true })
  getServiceReportById(
    { getState, setState }: StateContext<ServiceReportStateModel>,
    { id }: GetServiceReportById
  ) {
    return this.service.getServiceReportById(id).pipe(
      tap((serviceReport: IServiceReport) => {
        const state = getState();
        setState({
          ...state,
          serviceReport,
        });
      })
    );
  }

  @Action(CreateServiceReport, { cancelUncompleted: true })
  @OnSuccess({ message: 'serviceReportAdded' })
  createServiceReport(
    ctx: StateContext<ServiceReportStateModel>,
    { serviceReport }: CreateServiceReport
  ) {
    return this.service.createServiceReport(serviceReport).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(
          `${UserMaintenanceRoutes.Component}/${this.currentComponentId}`
        );
      })
    );
  }

  @Action(UpdateServiceReport, { cancelUncompleted: true })
  @OnSuccess({ message: 'serviceReportUpdated' })
  updateServiceReport(
    ctx: StateContext<ServiceReportStateModel>,
    { serviceReport }: UpdateServiceReport
  ) {
    return this.service.updateServiceReport(serviceReport).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(
          `${UserMaintenanceRoutes.Component}/${this.currentComponentId}`
        );
      })
    );
  }

  @Action(DeleteServiceReport, { cancelUncompleted: true })
  @OnSuccess({ message: 'serviceReportDeleted' })
  deleteServiceReport(ctx: StateContext<ServiceReportStateModel>, { id }: DeleteServiceReport) {
    return this.service.deleteServiceReport(id).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(
          `${UserMaintenanceRoutes.Component}/${this.currentComponentId}`
        );
      })
    );
  }

  @Action(PrintServiceReport, { cancelUncompleted: true })
  printServiceReport(
    { getState, setState }: StateContext<ServiceReportStateModel>,
    { id }: PrintServiceReport
  ) {
    return this.service.printServiceReport(id).pipe(
      tap((file: Blob) => {
        const state = getState();
        setState({
          ...state,
          serviceReportFile: file,
        });
      })
    );
  }

  @Selector()
  static getServiceReportById(state: ServiceReportStateModel) {
    return state.serviceReport;
  }

  @Selector()
  static getServiceReportFile(state: ServiceReportStateModel) {
    return state.serviceReportFile;
  }
}
